import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ChevronLeft, Music, Trash2, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlaylistDetail = () => {
  const { playlistId } = useParams();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlaylist = async () => {
      try {
        const res = await fetch(`${API}/playlists/${playlistId}`, {
          headers: getAuthHeaders(),
          
        });
        
        if (!res.ok) throw new Error('Playlist not found');
        
        const data = await res.json();
        setPlaylist(data.playlist);
        setTracks(data.tracks);
      } catch (error) {
        console.error('Error fetching playlist:', error);
        toast.error('Playlist not found');
        navigate('/profile');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlaylist();
  }, [playlistId, getAuthHeaders, navigate]);

  const removeTrack = async (trackId) => {
    try {
      const res = await fetch(`${API}/playlists/${playlistId}/tracks/${trackId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        
      });
      
      if (res.ok) {
        setTracks(prev => prev.filter(t => t.track_id !== trackId));
        toast.success('Track removed from playlist');
      }
    } catch (error) {
      toast.error('Failed to remove track');
    }
  };

  const getEmbedUrl = (url) => {
    if (url.includes('spotify.com') && !url.includes('/embed/')) {
      return url.replace('open.spotify.com/', 'open.spotify.com/embed/');
    }
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('soundcloud.com') && !url.includes('api.soundcloud.com')) {
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23CCFF00&auto_play=false`;
    }
    return url;
  };

  const isYouTubeUrl = (url) => {
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  const getEmbedHeight = (url) => {
    // YouTube videos need at least 315px height (standard 560x315 aspect ratio)
    if (isYouTubeUrl(url)) {
      return 315;
    }
    return 152;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-primary font-accent text-3xl">LOADING...</div>
      </div>
    );
  }

  if (!playlist) {
    return null;
  }

  return (
    <div className="min-h-screen py-8" data-testid="playlist-detail-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/profile" 
          className="inline-flex items-center text-muted-foreground hover:text-primary font-mono text-sm no-underline mb-8"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Profile
        </Link>

        {/* Header */}
        <div className="mb-12">
          <h1 className="font-accent text-5xl text-foreground mb-2">{playlist.name}</h1>
          {playlist.description && (
            <p className="font-mono text-sm text-muted-foreground">{playlist.description}</p>
          )}
          <p className="font-mono text-xs text-muted-foreground mt-2">
            {tracks.length} tracks
          </p>
        </div>

        {/* Tracks */}
        {tracks.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-border">
            <Music className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="font-mono text-muted-foreground mb-4">No tracks in this playlist</p>
            <Link to="/browse">
              <Button variant="outline" className="font-mono uppercase">
                Browse Bands to Add Tracks
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {tracks.map((track, index) => (
              <div 
                key={track.track_id}
                className="border-2 border-border bg-card p-6"
                data-testid={`playlist-track-${track.track_id}`}
              >
                {/* Track Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <span className="font-accent text-3xl text-muted-foreground">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h3 className="font-sans text-xl font-bold text-foreground">
                        {track.title}
                      </h3>
                      <p className="font-mono text-sm text-muted-foreground">
                        {track.band_name}
                      </p>
                      <Badge variant="outline" className="font-mono text-[10px] uppercase mt-1">
                        {track.track_type}
                      </Badge>
                    </div>
                  </div>
                  
                  <Button 
                    variant="ghost"
                    size="icon"
                    onClick={() => removeTrack(track.track_id)}
                    className="text-destructive hover:text-destructive/80"
                    data-testid={`remove-track-${track.track_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Embed Player */}
                <div className="border border-border bg-background">
                  <iframe
                    src={getEmbedUrl(track.embed_url)}
                    width="100%"
                    height={getEmbedHeight(track.embed_url)}
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                    className="border-0"
                    title={track.title}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaylistDetail;
